import React, { useEffect, useState } from 'react';
import { RefreshCw } from 'lucide-react';
import { AppHeader } from '../components/AppHeader';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { listAudit } from '../lib/api';
import { formatRp } from '../lib/format';

const ACTIONS = [
  { v: '', l: 'Semua aktivitas' },
  { v: 'create_entry', l: 'Buat entry setoran' },
  { v: 'update_entry', l: 'Ubah entry setoran' },
  { v: 'delete_entry', l: 'Hapus entry setoran' },
  { v: 'login_success', l: 'Login berhasil' },
  { v: 'login_failed', l: 'Login gagal' },
  { v: 'create_user', l: 'Tambah user' },
  { v: 'update_user', l: 'Ubah user' },
  { v: 'delete_user', l: 'Hapus user' },
  { v: 'create_branch', l: 'Tambah cabang' },
  { v: 'delete_branch', l: 'Hapus cabang' },
];

const actionLabel = (a) => ACTIONS.find((x) => x.v === a)?.l || a;

const actionColor = (a) => {
  if (a.includes('delete') || a === 'login_failed') return 'bg-red-50 text-red-800 border-red-200';
  if (a.includes('create') || a === 'login_success') return 'bg-emerald-50 text-emerald-800 border-emerald-200';
  if (a.includes('update') || a === 'change_password') return 'bg-amber-50 text-amber-800 border-amber-200';
  return 'bg-stone-50 text-stone-700 border-stone-200';
};

const renderDiff = (log) => {
  if (log.action === 'update_entry' && log.before && log.after) {
    const keys = Object.keys(log.after);
    const changes = keys.filter((k) => JSON.stringify(log.before[k]) !== JSON.stringify(log.after[k]));
    if (!changes.length) return <span className="text-stone-400">tidak ada perubahan</span>;
    return (
      <div className="space-y-0.5">
        {changes.map((k) => {
          const b = log.before[k];
          const a = log.after[k];
          const isMoney = ['saldo_awal', 'tunai', 'pending', 'debit', 'emoney', 'transaksi_system'].includes(k);
          return (
            <div key={k} className="text-xs">
              <span className="uppercase tracking-wider text-stone-500 mr-1">{k}:</span>
              <span className="text-red-700 line-through mr-1 num">
                {isMoney ? formatRp(b) : String(b ?? '—')}
              </span>
              <span className="text-emerald-800 num">
                {isMoney ? formatRp(a) : String(a ?? '—')}
              </span>
            </div>
          );
        })}
      </div>
    );
  }
  if (log.action === 'create_entry' && log.after) {
    return (
      <div className="text-xs text-stone-600 num">
        Tanggal {log.after.day}/{log.after.month}/{log.after.year} · Tunai {formatRp(log.after.tunai)}
      </div>
    );
  }
  if (log.action === 'delete_entry' && log.before) {
    return (
      <div className="text-xs text-stone-600 num">
        Tanggal {log.before.day}/{log.before.month}/{log.before.year} · Tunai {formatRp(log.before.tunai)}
      </div>
    );
  }
  if (log.extra) {
    return <span className="text-xs text-stone-500">{log.extra}</span>;
  }
  if (log.after?.username) {
    return <span className="text-xs text-stone-600">username: <b>{log.after.username}</b></span>;
  }
  return <span className="text-stone-400 text-xs">—</span>;
};

export default function AuditPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState('');
  const [usernameFilter, setUsernameFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (action) params.action = action;
      if (usernameFilter) params.username = usernameFilter.toLowerCase();
      const r = await listAudit(params);
      setLogs(r);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [action]);

  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader />
      <main className="max-w-[1400px] mx-auto px-6 md:px-8 py-8">
        <div className="mb-8">
          <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold mb-2">
            Jejak Perubahan
          </div>
          <h1 className="font-heading text-3xl font-semibold text-stone-900 tracking-tight">
            Audit Log
          </h1>
          <p className="text-sm text-stone-600 mt-2">
            Semua aktivitas tercatat: siapa, kapan, dan apa yang berubah.
          </p>
        </div>

        <div className="bg-white border border-stone-200 rounded-lg p-5 mb-4 flex flex-wrap gap-4 items-end">
          <div className="min-w-[220px]">
            <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
              Aktivitas
            </Label>
            <Select value={action || 'ALL'} onValueChange={(v) => setAction(v === 'ALL' ? '' : v)}>
              <SelectTrigger className="bg-white" data-testid="audit-action-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Semua aktivitas</SelectItem>
                {ACTIONS.filter((a) => a.v).map((a) => (
                  <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px]">
            <Label className="text-[11px] uppercase tracking-widest text-stone-600 mb-1.5 block">
              Username
            </Label>
            <Input
              value={usernameFilter}
              onChange={(e) => setUsernameFilter(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              placeholder="misal: kasir1"
              className="bg-white"
              data-testid="audit-username-input"
            />
          </div>
          <Button
            onClick={load}
            className="bg-emerald-800 hover:bg-emerald-900 text-white"
            data-testid="audit-refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-1" /> Muat Ulang
          </Button>
        </div>

        <div className="bg-white border border-stone-200 rounded-lg overflow-hidden">
          <table className="w-full ledger" data-testid="audit-table">
            <thead>
              <tr>
                <th style={{ width: '15%' }}>Waktu</th>
                <th style={{ width: '12%' }}>User</th>
                <th style={{ width: '15%' }}>Aktivitas</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((l) => (
                <tr key={l.id} data-testid={`audit-row-${l.id}`}>
                  <td className="text-xs text-stone-600 num">
                    {new Date(l.timestamp).toLocaleString('id-ID')}
                  </td>
                  <td className="text-sm text-stone-800 font-medium">
                    {l.actor_username || '—'}
                  </td>
                  <td>
                    <span className={`inline-block text-[11px] font-semibold px-2 py-1 rounded border ${actionColor(l.action)}`}>
                      {actionLabel(l.action)}
                    </span>
                  </td>
                  <td>{renderDiff(l)}</td>
                </tr>
              ))}
              {logs.length === 0 && !loading && (
                <tr>
                  <td colSpan={4} className="text-center text-stone-500 py-8">
                    Belum ada aktivitas.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
}
